<template>
  <div class="llr-page">
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Lucky LiveRunner</h1>
        <p class="pg-title-sub">Manage monthly lucky runner gift records and sent status</p>
      </div>
    </div>

    <div class="llr-toolbar">
      <div class="llr-filter">
        <span class="llr-filter-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4"/>
            <path d="M8 2v4"/>
            <path d="M3 10h18"/>
          </svg>
        </span>
        <div class="llr-month-picker">
          <vue-monthly-picker
            v-model="selectedMonth"
            :selectedBackgroundColor="selectedBackgroundColor"
            :monthLabels="locale"
            :dateFormat="dateFormat"
            @selected="handleSelect"
            placeHolder="All Time"
          >
          </vue-monthly-picker>
        </div>
      </div>

      <div class="llr-toolbar-actions">
        <button class="llr-add-btn" type="button" @click="showAddLuckyModal">
          <svg class="llr-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 5v14"/>
            <path d="M5 12h14"/>
          </svg>
          Add Manually
        </button>
        <runner-field-visibility-control
          modal-name="luckyLiveRunnerFieldVisibility"
          title="Lucky LiveRunner Fields"
          subtitle="Choose the columns shown on this page"
          :field-options="fieldOptions"
          :selected-fields="selectedFieldIds"
          @apply="applyFieldSelection"
          @reset="resetFieldSelection"
        />
      </div>
    </div>

    <div class="lucky-liverunner llr-card">
      <div v-if="loading" class="llr-skeleton-table">
        <div v-for="row in 4" :key="'llr-skeleton-' + row" class="llr-skeleton-row">
          <div v-for="cell in visibleLuckyColumns.length" :key="'llr-skeleton-' + row + '-' + cell" class="llr-skeleton-cell sk-shimmer"></div>
        </div>
      </div>

      <div v-else-if="tableData.length === 0" class="llr-empty">
        <span class="llr-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7"/>
            <path d="M2 7h20v5H2z"/>
            <path d="M12 22V7"/>
            <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z"/>
            <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z"/>
          </svg>
        </span>
        <h2 class="llr-empty-title">No lucky runners found</h2>
        <p class="llr-empty-text">Choose another month or add a lucky runner manually.</p>
      </div>

      <div v-else class="llr-table-wrap">
        <table class="llr-table">
          <thead>
            <tr>
              <th v-for="column in visibleLuckyColumns" :key="column.id" :class="luckyColumnClass(column.id)">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(runner, index) in tableData" :key="runner.id || index">
              <td v-for="column in visibleLuckyColumns" :key="column.id" :class="luckyColumnClass(column.id)">
                <span v-if="column.id === 'race-date'">{{ raceDate(runner) }}</span>
                <span v-else-if="column.id === 'run-start'">{{ raceTime(runner) }}</span>
                <a v-else-if="column.id === 'run-name'" class="llr-run-link" :href="pastRunHref(runner)" target="_blank">{{ displayValue(runner.runName) }}</a>
                <span v-else-if="column.id === 'trainer'">{{ displayValue(runner.trainerName) }}</span>
                <button v-else-if="column.id === 'runner-id'" class="llr-id-link" type="button" @click="openRunnerData(runner.runnerId)">{{ displayValue(runner.runnerId) }}</button>
                <span v-else-if="column.id === 'runner-name'">{{ displayValue(runner.runnerName) }}</span>
                <span v-else-if="column.id === 'sent-date'">{{ displayValue(runner.presentSentDate) }}</span>
                <span v-else-if="column.id === 'present-content'">{{ displayValue(runner.presentContent) }}</span>
                <span v-else-if="column.id === 'note'">{{ displayValue(runner.note) }}</span>
                <div v-else-if="column.id === 'actions'" class="llr-row-actions">
                  <button class="llr-icon-btn" type="button" title="Edit lucky runner" @click="editLuckyRunner(runner, index)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 20h9"/>
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                    </svg>
                  </button>
                  <button class="llr-icon-btn llr-icon-btn--danger" type="button" title="Delete lucky runner" @click="deleteLuckyRunner(runner, index)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 6h18"/>
                      <path d="M8 6V4h8v2"/>
                      <path d="M19 6l-1 14H6L5 6"/>
                      <path d="M10 11v5"/>
                      <path d="M14 11v5"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="llr-pagination-wrap">
          <v-pagination @page-change="pageChange" @page-size-change="pageSizeChange" :pageIndex ="pageIndex+1" :total="pageTotal" :page-size="pageSize" :layout="['total', 'prev', 'pager', 'next', 'sizer', 'jumper']"></v-pagination>
      </div>

      <edit-lucky-live-runner :runnerData = "runnerData" :show="showEditModal" @saved="onSaved"  @closed="onClosed"></edit-lucky-live-runner> 
      
      <add-lucky-live-runner :show="showAddModal" @saved="onSaved"  @closed="onClosed"></add-lucky-live-runner> 
    
    </div>
  </div>
</template>

<script>
let dialogOptions = {
    html: false, // set to true if your message contains HTML tags. eg: "Delete <b>Foo</b> ?"
    loader: false, // set to true if you want the dailog to show a loader after click on "proceed"
    reverse: false, // switch the button positions (left to right, and vise versa)
    cancelText: 'No',
    okText: 'Yes',
    type: 'basic', // coming soon: 'soft', 'hard'
    verification: 'continue', // for hard confirm, user will be prompted to type this to enable the proceed button
    clicksCount: 3,
    animation: 'bounce', // for soft confirm, user will be asked to click on "proceed" btn 3 times before actually proceeding
  }
import LuckyLiveRunnerService from "../../../services/luckyLiveRunner"
import EditLuckyLiveRunner from './EditLuckyLiveRunner'
import AddLuckyLiveRunner from './AddLuckyLiveRunner'
import VueMonthlyPicker from 'vue-monthly-picker'
import RunnerFieldVisibilityControl from './RunnerFieldVisibilityControl'
import {
  loadRunnerFieldPreference,
  resetRunnerFieldPreference,
  runnerFieldPreferencePages,
  saveRunnerFieldPreference
} from '@/utils/runnerFieldPreferences'
//import moment from 'moment-timezone'
import moment from 'moment'
const fieldPreferenceConfig = runnerFieldPreferencePages.luckyLiveRunners
export default {
  name: 'LuckyLiveRunners',
  data: () => ({
  tableData:[],
  pageIndex:0,
  pageTotal:null,
  pageSize:10,  
  startDate:moment().startOf('month').toISOString(),
  endDate:moment().endOf('month').toISOString(),
  showEditModal:false,  
  runnerData:{},
  selectedMonth: moment(),
  selectedBackgroundColor: '#423799',  
  locale: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],  
  dateFormat:"MMMM , YYYY",
  showAddModal:false,
  loading:false,
  selectedFieldIds: loadRunnerFieldPreference(fieldPreferenceConfig.storageKey, fieldPreferenceConfig.fields)
  }),
  computed: {
    fieldOptions() {
      return fieldPreferenceConfig.fields
    },
    visibleLuckyColumns() {
      return this.fieldOptions.filter(function (field) {
        return this.isFieldVisible(field.id)
      }, this)
    }
  },
  methods: {
    getAllLuckyRunners(startDate,endDate,page,size){
      this.loading=true
      LuckyLiveRunnerService.getLuckyLiveRunners(startDate,endDate,page,size).then(response => {         
        this.tableData=response.data.content || []
        this.pageTotal = response.data.totalElements
        this.loading=false
      }).catch(error => {
        this.loading=false
        console.log("err--->>",error)})
    },
    displayValue(value, fallback) {
      const emptyFallback = fallback || '-'
      if (value === 0) return '0'
      if (value === false) return 'No'
      if (value === true) return 'Yes'
      if (value === null || value === undefined) return emptyFallback
      const stringValue = String(value).trim()
      return stringValue ? stringValue : emptyFallback
    },
    raceDate(runner) {
      if (!runner || !runner.raceDateTime) return '-'
      return moment(runner.raceDateTime).format('YYYY-MM-DD')
    },
    raceTime(runner) {
      if (!runner || !runner.raceDateTime) return '-'
      return moment(runner.raceDateTime).format('HH:mm:ss')
    },
    pastRunHref(runner) {
      if (!runner || !runner.raceId) return '#'
      return '#/admin/past-run/detail/' + runner.raceId
    },
    isFieldVisible(fieldId) {
      return this.selectedFieldIds.indexOf(fieldId) !== -1
    },
    luckyColumnClass(fieldId) {
      var className = 'llr-col-' + fieldId
      if (fieldId === 'note') return className + ' llr-note-cell'
      if (fieldId === 'actions') return className + ' llr-actions-cell'
      return className
    },
    applyFieldSelection(selectedIds) {
      this.selectedFieldIds = saveRunnerFieldPreference(fieldPreferenceConfig.storageKey, this.fieldOptions, selectedIds)
    },
    resetFieldSelection() {
      this.selectedFieldIds = resetRunnerFieldPreference(fieldPreferenceConfig.storageKey, this.fieldOptions)
    },
    openRunnerData(runnerId) {
      if (!runnerId) return
      let route = this.$router.resolve({
        name: 'runner-data',
        params: {
          runnerId: runnerId
        }
      })
      window.open(route.href, '_blank')
    },
    editLuckyRunner(rowData, index) {
      this.showEditModal = true
      this.runnerData = { columeName: 'edit', rowData: rowData, index: index }
    },
    deleteLuckyRunner(rowData, index) {
      this.$dialog.confirm("Are you sure you want to delete？", dialogOptions)
          .then((dialog) => {
            LuckyLiveRunnerService.deleteLuckyLiveRunner(rowData.id).then(response => {
            this.$delete(this.tableData,index)
            }).catch(error => {
                console.log("err--->",error) 
              }) 
          })
    },
    pageChange(pageIndex){
      this.pageIndex = pageIndex-1
      this.getAllLuckyRunners(this.startDate,this.endDate,this.pageIndex,this.pageSize)        
    },
    pageSizeChange(pageSize){
      this.pageIndex = 0
      this.pageSize = pageSize
      this.getAllLuckyRunners(this.startDate,this.endDate,this.pageIndex, this.pageSize)        
    },
    onSaved(data){
      this.showEditModal = false
      this.showAddModal=false
      this.getAllLuckyRunners(this.startDate,this.endDate,this.pageIndex, this.pageSize)  
    },
    onClosed(){
      this.showEditModal = false
      this.showAddModal=false
    },
    showAddLuckyModal(){
      this.showAddModal=true
    },
    handleSelect (value) {
      this.pageIndex = 0
      //show result for selected month
      if(value!=null){       
        var monthYear = moment([value.year(), value.month()])
        this.startDate= moment(monthYear).startOf('month').toISOString()
        this.endDate =  moment(monthYear).endOf('month').toISOString()  
      }else{ 
        //if no month year selected then show all        
        this.startDate= ''
        this.endDate =  ''
      }
      this.getAllLuckyRunners(this.startDate,this.endDate,this.pageIndex, this.pageSize) 
    },
    
  },
  created() { 
    //show result for current month
    this.getAllLuckyRunners(this.startDate,this.endDate,this.pageIndex, this.pageSize)
  },
  mounted () {
    
  },
  components: {
    EditLuckyLiveRunner,
    VueMonthlyPicker,
    AddLuckyLiveRunner,
    RunnerFieldVisibilityControl
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.llr-page {
  color: #1a1a2e;
  font-family: 'Inter', sans-serif;
}

.llr-toolbar,
.llr-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.llr-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
  padding: 1rem 1.25rem;
}

.llr-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.llr-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.llr-filter-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.llr-filter-icon svg {
  width: 16px;
  height: 16px;
}

.llr-month-picker {
  width: 280px;
  max-width: 100%;
}

.llr-month-picker .month-year-display.input {
  min-height: 36px;
  border: 1.5px solid #e0e0ea;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  box-shadow: none;
}

.llr-month-picker .active {
  background: #5b4fcf;
}

.llr-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: #5b4fcf;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 0 18px;
  transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
}

.llr-add-btn:hover {
  background: #4338ca;
  box-shadow: 0 4px 16px rgba(91, 79, 207, 0.35);
  transform: translateY(-1px);
}

.llr-btn-icon {
  width: 13px;
  height: 13px;
}

.llr-card {
  overflow: hidden;
  padding: 1rem;
}

.llr-table-wrap {
  overflow-x: auto;
}

.llr-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 760px;
  table-layout: fixed;
}

.llr-table th {
  background: #413b94;
  border-right: 1px solid rgba(255,255,255,0.22);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  height: 38px;
  line-height: 1.15;
  padding: 0 10px;
  text-align: left;
  text-transform: uppercase;
}

.llr-table th:first-child {
  border-top-left-radius: 9px;
}

.llr-table th:last-child {
  border-right: none;
  border-top-right-radius: 9px;
}

.llr-table td {
  border-bottom: 1px solid #f0f0f5;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  padding: 10px;
  vertical-align: middle;
}

.llr-table tbody tr:hover {
  background: #fafafa;
}

.llr-table th:nth-child(1),
.llr-table th:nth-child(2),
.llr-table td:nth-child(1),
.llr-table td:nth-child(2) {
  width: 92px;
}

.llr-table th:nth-child(3),
.llr-table td:nth-child(3) {
  width: 230px;
}

.llr-table th:nth-child(4),
.llr-table td:nth-child(4) {
  width: 90px;
}

.llr-table th:nth-child(5),
.llr-table td:nth-child(5) {
  width: 86px;
}

.llr-table th:nth-child(6),
.llr-table td:nth-child(6) {
  width: 150px;
}

.llr-table th:nth-child(7),
.llr-table td:nth-child(7) {
  width: 110px;
}

.llr-table th:nth-child(8),
.llr-table td:nth-child(8) {
  width: 190px;
}

.llr-table th:nth-child(10),
.llr-table td:nth-child(10) {
  width: 92px;
  text-align: center;
}

.llr-table th.llr-col-race-date,
.llr-table td.llr-col-race-date,
.llr-table th.llr-col-run-start,
.llr-table td.llr-col-run-start {
  width: 92px;
}

.llr-table th.llr-col-run-name,
.llr-table td.llr-col-run-name {
  width: 230px;
}

.llr-table th.llr-col-trainer,
.llr-table td.llr-col-trainer {
  width: 90px;
}

.llr-table th.llr-col-runner-id,
.llr-table td.llr-col-runner-id {
  width: 86px;
}

.llr-table th.llr-col-runner-name,
.llr-table td.llr-col-runner-name {
  width: 150px;
}

.llr-table th.llr-col-sent-date,
.llr-table td.llr-col-sent-date {
  width: 110px;
}

.llr-table th.llr-col-present-content,
.llr-table td.llr-col-present-content {
  width: 190px;
}

.llr-table th.llr-col-actions,
.llr-table td.llr-col-actions {
  width: 92px;
  text-align: center;
}

.llr-run-link,
.llr-id-link {
  color: #0785e8;
  font-weight: 700;
  text-decoration: none;
}

.llr-run-link:hover,
.llr-id-link:hover {
  color: #4338ca;
}

.llr-id-link {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.llr-note-cell {
  color: #4d4d61;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.llr-row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.llr-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #e8e8ed;
  border-radius: 50%;
  background: #fff;
  color: #5b4fcf;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.llr-icon-btn svg {
  width: 14px;
  height: 14px;
}

.llr-icon-btn:hover {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.llr-icon-btn--danger {
  color: #c0392b;
}

.llr-icon-btn--danger:hover {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.llr-pagination-wrap {
  border-top: 1px solid #f0f0f5;
  margin-top: 1rem;
  padding-top: 1rem;
}

.llr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  text-align: center;
}

.llr-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.llr-empty-icon svg {
  width: 16px;
  height: 16px;
}

.llr-empty-title {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  margin: 14px 0 5px;
}

.llr-empty-text {
  color: #8a8a9a;
  font-size: 13px;
  margin: 0;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.llr-skeleton-table {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.llr-skeleton-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(78px, 1fr));
  gap: 8px;
}

.llr-skeleton-cell {
  height: 36px;
}

@media (max-width: 700px) {
  .llr-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .llr-filter {
    align-items: flex-start;
  }

  .llr-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .llr-month-picker {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// AllLuckyLiveRunners.vue?4d3665fc